import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import dayjs from "dayjs"
import { CNav, CNavItem, CNavLink } from "@coreui/react"
import AdminTable from "../Helpers/AdminTable"
import TableTitle from "../Helpers/TableTitle"
import SureModal from "src/components/Modals/SureModal"
import { lockersAction } from "src/store/Slices/Administrator/LockersSlice"
import AddEditLockerModal from "./Modals/AddEditLockerModal"
import LockerActions from "./LockerActions"
import LockerRents from "./LockerRents"

const FALLBACK_COLUMNS = {
  data: {
    value: {
      columns: [
        { title: "№", dataIndex: "id" },
        { title: "Клуб", dataIndex: "clubName" },
        { title: "Номер", dataIndex: "number" },
        { title: "Заблокирован", dataIndex: "isBlocked" },
        { title: "Причина", dataIndex: "blockReason" },
        { title: "Занят", dataIndex: "isOccupied" },
        { title: "Телефон", dataIndex: "currentUserPhone" },
        { title: "Аренда до", dataIndex: "rentEndDate" },
        { title: "Цена", dataIndex: "price" },
        { title: "Лок. закрыть", dataIndex: "localOn" },
        { title: "Лок. открыть", dataIndex: "localOff" },
        { title: "Лок. импульс", dataIndex: "localPulse" },
        { title: "Сет. закрыть", dataIndex: "externalOn" },
        { title: "Сет. открыть", dataIndex: "externalOff" },
        { title: "Сет. импульс", dataIndex: "externalPulse" },
      ],
    },
  },
}

const yesNo = (value) => (value ? "Да" : "Нет")

const Lockers = ({ mnemo }) => {
  const dispatch = useDispatch()
  const buttonCodes = ["insertBtn", "updateBtn", "deleteBtn", "isDeletedBtn"]
  const hasButtonRights = !!mnemo?.children?.some((el) =>
    buttonCodes.includes(el.mnemo),
  )
  const allowed = (code) =>
    !mnemo ||
    !hasButtonRights ||
    !!mnemo?.children?.find((el) => el.mnemo === code)

  const [tab, setTab] = useState("directory")
  const [isDeleted, setIsDeleted] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [lockerInfo, setLockerInfo] = useState()
  const [tableFilters, setTableFilters] = useState({})
  const [isOpenAddEditModal, setIsOpenAddEditModal] = useState({
    open: false,
    flag: "",
  })
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)

  const lockersList = useSelector((state) => state.LockersSlice.lockersList)
  const lockersListLoading = useSelector(
    (state) => state.LockersSlice.lockersListLoading,
  )

  const items = [
    {
      key: "1",
      label: "Редактировать",
      onClick: () => setIsOpenAddEditModal({ open: true, flag: "edit" }),
      disabled: !allowed("updateBtn"),
    },
    {
      key: "2",
      label: "Удалить",
      onClick: () => setIsOpenDeleteModal(true),
      disabled: !allowed("deleteBtn"),
    },
  ]

  const load = (nextPage = page, nextPageSize = pageSize, filters = tableFilters, deleted = isDeleted) => {
    dispatch(
      lockersAction({
        flag: "get",
        value: {
          isDeleted: deleted,
          count: nextPageSize,
          offset: nextPageSize * (nextPage - 1),
          ...filters,
        },
      }),
    )
  }

  useEffect(() => {
    load(1, 20, {}, false)
  }, [])

  const searchItemsMap = (keys, values) => {
    const formattedItems = {}
    if (!keys || !values) return formattedItems
    keys.forEach((key, index) => {
      const selected = values[index]
      if (selected == undefined) return
      if (key === "id") {
        if (selected[0] !== undefined && selected[0] !== "") {
          formattedItems.idFrom = selected[0]
        }
        if (selected[1] !== undefined && selected[1] !== "") {
          formattedItems.idTo = selected[1]
        }
      } else if (key === "clubName" || key === "clubId" || key === "club") {
        if (selected[0] != undefined) formattedItems.clubId = selected[0]
      } else if (key === "number") {
        if (selected[0] != undefined && selected[0] !== "") {
          formattedItems.number = selected[0]
        }
      } else if (key === "isBlocked") {
        if (selected[0] != undefined) formattedItems.isBlocked = selected[0]
      }
    })
    return formattedItems
  }

  const handleTableChange = (pagination, filters, sorter) => {
    const nextFilters = {
      ...searchItemsMap(Object.keys(filters), Object.values(filters)),
      orderBy: sorter?.column?.dataIndex,
      isDesc: sorter?.order ? sorter.order === "ascend" : undefined,
    }
    setTableFilters(nextFilters)
    setPage(pagination.current)
    setPageSize(pagination.pageSize)
    load(pagination.current, pagination.pageSize, nextFilters)
  }

  const showDeletedItems = (value) => {
    setIsDeleted(value)
    setPage(1)
    load(1, pageSize, tableFilters, value)
  }

  const onClickDelete = () => {
    dispatch(lockersAction({ flag: "delete", value: { id: lockerInfo?.id } })).then(
      (result) => {
        if (result.payload?.response || result.payload?.data?.isSuccess === false) {
          return
        }
        setIsOpenDeleteModal(false)
        setPage(1)
        load(1, pageSize, tableFilters)
      },
    )
  }

  const rows = lockersList?.data?.value?.rows?.map((row) => ({
    ...row,
    isBlocked: yesNo(row.isBlocked),
    isOccupied: yesNo(row.isOccupied),
    rentEndDate: row.rentEndDate
      ? dayjs(row.rentEndDate).format("DD.MM.YYYY HH:mm")
      : "",
  }))

  const columns = lockersList?.data?.value?.columns?.length
    ? lockersList
    : FALLBACK_COLUMNS

  return (
    <>
      <CNav variant='tabs' style={{ marginBottom: 12 }}>
        <CNavItem>
          <CNavLink
            style={{ cursor: "pointer" }}
            active={tab === "directory"}
            onClick={() => setTab("directory")}
          >
            Справочник
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            style={{ cursor: "pointer" }}
            active={tab === "journal"}
            onClick={() => setTab("journal")}
          >
            Журнал команд
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            style={{ cursor: "pointer" }}
            active={tab === "rents"}
            onClick={() => setTab("rents")}
          >
            Аренда
          </CNavLink>
        </CNavItem>
      </CNav>
      {tab === "journal" ? (
        <LockerActions />
      ) : tab === "rents" ? (
        <LockerRents />
      ) : (
        <>
      <AddEditLockerModal
        visible={isOpenAddEditModal}
        setVisible={setIsOpenAddEditModal}
        lockerInfo={lockerInfo}
        setPage={setPage}
        setPageSize={setPageSize}
        reload={() => load(1, pageSize, tableFilters)}
      />
      <SureModal
        visible={isOpenDeleteModal}
        setVisible={setIsOpenDeleteModal}
        onClickYes={onClickDelete}
      />
      <AdminTable
        scroll={{ x: 1600, y: 600 }}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onChange={handleTableChange}
        loading={lockersListLoading}
        onClickRowOne={setLockerInfo}
        columns={columns}
        rows={rows}
        total={lockersList?.data?.value?.totalCount}
        title={() => (
          <TableTitle
            isDeleted={isDeleted}
            showDeletedItems={showDeletedItems}
            onClickOpenModal={() =>
              setIsOpenAddEditModal({ open: true, flag: "add" })
            }
            mnemoButton={!allowed("insertBtn")}
            mnemoSwitcher={!allowed("isDeletedBtn")}
          />
        )}
        items={items}
        isDeleted={isDeleted}
      />
        </>
      )}
    </>
  )
}

export default Lockers
